import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { delay } from '@gametailors/web-core/misc';
import { TAudioSource } from '../../components/rid-audio-button/rid-audio-button';
import { RidBottomButton } from '../../components/rid-bottom-button/rid-bottom-button';
import { RidCorrectAnswerModal } from '../../components/rid-correct-answer-modal/rid-correct-answer-modal';
import { RidMechanicWrapper } from '../../components/rid-mechanic-wrapper/rid-mechanic-wrapper';
import { RidModalBackground } from '../../components/rid-modal-background/rid-modal-background';
import { RidOverlayFeedback } from '../../components/rid-overlay-feedback/rid-overlay-feedback';
import { RidTopBar } from '../../components/rid-top-bar/rid-top-bar';
import { PronunciationService } from '../../services/pronunciation.service';
import { AudioEffectPlayer, AudioPlayer } from '../../utils/audio.utils';
import { evaluateLingoGuess, getLingoKeyStates, TLingoTileResult } from '../../utils/lingo.utils';
import { RidLingoKeyboard, TLingoKlank } from './rid-lingo-keyboard/rid-lingo-keyboard';
import { RidLingoMarquee, TLingoMarqueeMood } from './rid-lingo-marquee/rid-lingo-marquee';
import { RidLingoPresenter, TLingoPresenterMood } from './rid-lingo-presenter/rid-lingo-presenter';
import { RidLingoSpotlight, TLingoSpotlightMood } from './rid-lingo-spotlight/rid-lingo-spotlight';

export interface TLingoAssignment {
  word: string;
  // Always exactly four klanken, so every board has the same width
  klanken: [TLingoKlank, TLingoKlank, TLingoKlank, TLingoKlank];
  audio?: TAudioSource;
}

type TLingoTile =
  | { state: 'EMPTY' }
  | { state: 'TYPED'; klank: TLingoKlank }
  | { state: 'GHOST'; klank: TLingoKlank }
  | { state: 'HIDDEN' | TLingoTileResult; klank: TLingoKlank };

interface TLingoGuess {
  klanken: TLingoKlank[];
  results: TLingoTileResult[];
  // How many tiles of this guess have been flipped over so far, so they can be revealed one by one.
  revealed: number;
}

/*
 * Hanging from the ceiling (drop in Tailwind spacing units), in two sets: one spread evenly over the screen and aimed at
 * the board, one spread evenly over the presenter and aimed at them. While a guess is checked, both are aimed at the board.
 */
const SPOTLIGHTS = [
  { drop: 12, color: '#ff5fa8', target: 'SCREEN' },
  { drop: 5, color: '#3fd0ff', target: 'SCREEN' },
  { drop: 5, color: '#ffd23f', target: 'SCREEN' },
  { drop: 12, color: '#ff5fa8', target: 'SCREEN' },
  { drop: 12, color: '#3fd0ff', target: 'PRESENTER' },
  { drop: 5, color: '#ffd23f', target: 'PRESENTER' },
  { drop: 5, color: '#ff5fa8', target: 'PRESENTER' },
  { drop: 12, color: '#3fd0ff', target: 'PRESENTER' },
] as const satisfies { drop: number; color: string; target: 'SCREEN' | 'PRESENTER' }[];

// The width of the stage in Tailwind spacing units. Keep in sync with rid-lingo.html.
const STAGE_WIDTH = 660;
// The height of the room below the presenter where the button to the next word is shown, in px. Keep in sync with rid-lingo.html.
const BUTTON_ROOM = 160;

interface TBox {
  left: number;
  top: number;
  width: number;
  height: number;
}
const EMPTY_BOX: TBox = { left: 0, top: 0, width: 0, height: 0 };

const SPOTLIGHT_MOODS = {
  INPUT: 'IDLE',
  EVALUATING: 'FOCUS',
  WON: 'PARTY',
  LOST: 'DIM',
} as const satisfies Record<string, TLingoSpotlightMood>;

const SCREEN_MOODS = {
  INPUT: 'IDLE',
  EVALUATING: 'IDLE',
  WON: 'CHASE',
  LOST: 'DIM',
} as const satisfies Record<string, TLingoMarqueeMood>;

const ROOM_MOODS: Record<TLingoSpotlightMood, string> = {
  IDLE: 'brightness-90',
  FOCUS: 'brightness-70',
  PARTY: 'brightness-110 saturate-110',
  DIM: 'brightness-45 saturate-80',
};

// Sized by --tile, which the board sets
const TILE = 'grid size-(--tile) place-items-center rounded-[12%] font-default font-bold leading-none select-none';
const TILE_TEXT = 'text-[length:calc(var(--tile)*0.52)]';
const TILE_RAISED = 'shadow-[inset_0_-0.1em_0_rgb(0_0_0/0.18),inset_0_0.06em_0_rgb(255_255_255/0.3)]';
const TILE_STATES: Record<TLingoTile['state'], string> = {
  EMPTY: 'bg-[#34bec6] shadow-[inset_0_0_0_0.06em_rgb(20_120_130/0.35)]',
  TYPED: `bg-[#eef8fa] text-[#1c4f57] ${TILE_RAISED}`,
  HIDDEN: `bg-[#eef8fa] text-[#1c4f57] ${TILE_RAISED}`,
  // An empty slot, with the klank already found there as a faint reminder
  GHOST: 'bg-[#34bec6] text-[rgb(76_175_39/0.55)] shadow-[inset_0_0_0_0.06em_rgb(76_175_39/0.8)]',
  CORRECT: `bg-linear-to-b from-[#6dce3f] to-[#4caf27] text-white text-shadow-[0_0.06em_0_rgb(0_0_0/0.25)] ${TILE_RAISED}`,
  PRESENT: `bg-linear-to-b from-[#ffd43f] to-[#f4b400] text-white text-shadow-[0_0.06em_0_rgb(0_0_0/0.25)] ${TILE_RAISED}`,
  ABSENT: 'bg-[#34bec6] text-[rgb(15_90_100/0.45)] shadow-[inset_0_0_0_0.06em_rgb(20_120_130/0.35)]',
};

const TILE_REVEAL_INTERVAL = 350;
// Matches the duration-700 of the moves in rid-lingo.html
const LAMP_MOVE_DURATION = 700;
// A moment of suspense before the tiles start flipping, while the screen moves to the middle
const REVEAL_DELAY = 800;

@Component({
  selector: 'rid-lingo',
  imports: [
    RidMechanicWrapper,
    RidTopBar,
    RidLingoKeyboard,
    RidLingoMarquee,
    RidLingoSpotlight,
    RidLingoPresenter,
    RidBottomButton,
    RidOverlayFeedback,
    RidModalBackground,
    RidCorrectAnswerModal,
  ],
  templateUrl: './rid-lingo.html',
  host: {
    // The height comes from whoever hosts the mechanic (the full dialog in the dashboard, the viewport in the kitchen sink)
    class: 'block',
  },
})
export class RidLingo implements OnInit {
  private readonly $pronunciationService = inject(PronunciationService);
  private readonly destroyRef = inject(DestroyRef);

  assignments = input.required<TLingoAssignment[]>();
  /** Number of guesses (rows on the board) per word. */
  attempts = input.required<number>();
  /** Gives away the first klank of the word, like in the tv show. */
  revealFirstKlank = input<boolean>(true);

  readonly SPOTLIGHTS = SPOTLIGHTS;

  index = signal(0);
  assignment = computed(() => this.assignments()[this.index()]);
  answer = computed(() => this.assignment().klanken);

  guesses = signal<TLingoGuess[]>([]);
  // What's typed in the active row, filled from left to right.
  input = signal<(TLingoKlank | undefined)[]>([]);
  phase = signal<'INPUT' | 'EVALUATING' | 'WON' | 'LOST'>('INPUT');
  presenterMood = signal<TLingoPresenterMood>('IDLE');

  // Only counts flipped tiles, so the keyboard doesn't give anything away before the board does.
  keyStates = computed(() =>
    getLingoKeyStates(this.guesses().map((g) => ({ klanken: g.klanken, results: g.results.slice(0, g.revealed) }))),
  );

  // Positions the player has already found (plus the first klank, if it's given away): these are shown as ghosts on every new row.
  hints = computed<Map<number, TLingoKlank>>(() => {
    const hints = new Map<number, TLingoKlank>();
    if (this.revealFirstKlank()) hints.set(0, this.answer()[0]);

    for (const guess of this.guesses()) {
      guess.results.forEach((result, i) => {
        if (result === 'CORRECT' && i < guess.revealed) hints.set(i, guess.klanken[i]);
      });
    }

    return hints;
  });

  isRowComplete = computed(() => this.input().every((k) => k !== undefined));
  canErase = computed(() => this.input().some((k) => k !== undefined));
  isLast = computed(() => this.index() === this.assignments().length - 1);

  board = computed<TLingoTile[][]>(() => {
    const columns = this.answer().length;
    const rows: TLingoTile[][] = this.guesses().map((guess) =>
      guess.klanken.map((klank, i) => ({ state: i < guess.revealed ? guess.results[i] : 'HIDDEN', klank })),
    );

    if (this.phase() === 'INPUT') {
      rows.push(
        this.input().map((klank, i): TLingoTile => {
          if (klank !== undefined) return { state: 'TYPED', klank };

          const hint = this.hints().get(i);
          return hint === undefined ? { state: 'EMPTY' } : { state: 'GHOST', klank: hint };
        }),
      );
    }

    while (rows.length < this.attempts()) {
      rows.push(Array.from({ length: columns }, () => ({ state: 'EMPTY' })));
    }

    return rows;
  });

  spotlightMood = computed<TLingoSpotlightMood>(() => SPOTLIGHT_MOODS[this.phase()]);
  marqueeMood = computed<TLingoMarqueeMood>(() => (this.isRowComplete() ? 'CHASE' : 'IDLE'));
  screenMood = computed<TLingoMarqueeMood>(() => SCREEN_MOODS[this.phase()]);
  roomClass = computed(() => ROOM_MOODS[this.spotlightMood()]);

  // The viewport, and the (unscaled) stage with the boxes on it, in px
  private readonly layout = signal({
    width: 0,
    height: 0,
    stage: { width: 0, height: 0, padding: 0, gap: 0 },
    screen: EMPTY_BOX,
    presenter: EMPTY_BOX,
    controls: EMPTY_BOX,
    // Where the presenter and the desk start within the presenter's box, which is taller to fill the side
    presenterContentTop: 0,
  });

  /*
   * While a guess is checked (and after the word is done), the keyboard is put away and the screen moves to the middle.
   * The presenter steps aside to make room for it, shrinking just enough to still fit next to it.
   */
  isFocused = computed(() => this.phase() !== 'INPUT');

  private readonly focus = computed(() => {
    const { stage, screen, presenter, controls, presenterContentTop } = this.layout();
    const screenX = stage.width / 2 - (screen.left + screen.width / 2);

    // Scaled around its bottom right corner, which moves to the right of the stage. Centred next to the screen.
    const left = stage.width / 2 + screen.width / 2 + stage.gap;
    const right = stage.width - stage.padding;
    const presenterScale = presenter.width === 0 ? 1 : Math.min(1, (right - left) / presenter.width);
    const contentHeight = (presenter.height - presenterContentTop) * presenterScale;
    const bottom = screen.top + screen.height / 2 + contentHeight / 2;
    const presenterX = right - (presenter.left + presenter.width);
    const presenterY = bottom - (presenter.top + presenter.height);

    // Centred right below the presenter, once the word is guessed
    const buttonX = right - (presenter.width * presenterScale) / 2 - (controls.left + controls.width / 2);
    const buttonY = bottom + BUTTON_ROOM - (controls.top + controls.height);

    return {
      screen: screenX,
      presenter: { x: presenterX, y: presenterY, scale: presenterScale },
      button: { x: buttonX, y: buttonY },
    };
  });

  screenShift = computed(() => (this.isFocused() ? this.focus().screen : 0));
  presenterTransform = computed(() => (this.isFocused() ? this.focus().presenter : { x: 0, y: 0, scale: 1 }));
  buttonShift = computed(() => this.focus().button);

  // How much the stage is scaled to fit the viewport
  scale = computed(() => {
    const { width, height, stage } = this.layout();
    return stage.width === 0 ? 0 : Math.min(width / stage.width, height / stage.height);
  });

  // Where the spotlights aim on the stage, and the stretch of stage they hang above, following the screen and the presenter around
  private readonly targets = computed(() => {
    const { screen, presenter, presenterContentTop } = this.layout();
    const { x, y, scale } = this.presenterTransform();
    // Roughly at the presenter's face, a third down from the top of the picture; moved along with its bottom right corner
    const right = presenter.left + presenter.width;
    const bottom = presenter.top + presenter.height;
    const contentHeight = presenter.height - presenterContentTop;

    const board = { x: screen.left + screen.width / 2 + this.screenShift(), y: screen.top + screen.height / 2 };
    const face = { x: right - (presenter.width / 2) * scale + x, y: bottom - contentHeight * 0.7 * scale + y };

    /*
     * The screen's set stays where the screen is while typing, and the presenter's set moves along with the presenter.
     * While the screen is in the middle, both sets are aimed at it from either side.
     */
    return {
      SCREEN: { ...board, from: screen.left, to: screen.left + screen.width },
      PRESENTER: { ...(this.isFocused() ? board : face), from: right - presenter.width * scale + x, to: right + x },
    };
  });

  // Places every spotlight in its set (in px from the left of the viewport) and aims it at its target
  spotlights = computed(() => {
    const { width, height, stage } = this.layout();
    const targets = this.targets();
    const scale = this.scale();
    // The size of a Tailwind spacing unit on screen
    const unit = (stage.width / STAGE_WIDTH) * scale;
    const stageLeft = (width - stage.width * scale) / 2;
    const stageTop = (height - stage.height * scale) / 2;

    return SPOTLIGHTS.map((spotlight, index) => {
      const target = targets[spotlight.target];
      const set = SPOTLIGHTS.filter((s) => s.target === spotlight.target);
      const place = SPOTLIGHTS.slice(0, index).filter((s) => s.target === spotlight.target).length;
      // Evenly spread, with only a quarter of the space between them at either end, so they're spaced out a bit more
      const left = stageLeft + (target.from + ((place + 0.25) / (set.length - 0.5)) * (target.to - target.from)) * scale;

      const dx = stageLeft + target.x * scale - left;
      const dy = stageTop + target.y * scale - spotlight.drop * unit;
      // Clockwise from straight down
      return { left, angle: (-Math.atan2(dx, dy) * 180) / Math.PI };
    });
  });

  overlayFeedback = viewChild.required<RidOverlayFeedback>('overlayFeedback');
  private readonly viewport = viewChild.required<ElementRef<HTMLElement>>('viewport');
  private readonly stage = viewChild.required<ElementRef<HTMLElement>>('stage');
  private readonly screen = viewChild.required('screen', { read: ElementRef<HTMLElement> });
  private readonly side = viewChild.required<ElementRef<HTMLElement>>('side');
  private readonly presenter = viewChild.required('presenter', { read: ElementRef<HTMLElement> });
  private readonly controls = viewChild.required<ElementRef<HTMLElement>>('controls');

  finished = output<void>();

  /*
   * The spotlights drop down from the ceiling once they're in place, and only slide sideways when the layout switches
   * between typing and checking. Otherwise they're just put in place, so they don't slide around while the dialog
   * they're in opens or resizes.
   */
  private readonly hasLampsEntered = signal(false);
  private readonly areLampsMoving = signal(false);
  lampClass = computed(() => {
    if (!this.hasLampsEntered()) return '-translate-y-48 opacity-0';
    return this.areLampsMoving() ? 'transition-[left,translate,opacity]' : 'transition-[translate,opacity]';
  });

  constructor() {
    let isFirstRun = true;
    let movingTimeout: ReturnType<typeof setTimeout> | undefined;
    effect(() => {
      this.isFocused();
      if (isFirstRun) {
        isFirstRun = false;
        return;
      }

      untracked(() => this.areLampsMoving.set(true));
      clearTimeout(movingTimeout);
      // A bit longer than the move itself, so it isn't cut short
      movingTimeout = setTimeout(() => this.areLampsMoving.set(false), LAMP_MOVE_DURATION + 100);
    });
    this.destroyRef.onDestroy(() => clearTimeout(movingTimeout));

    afterNextRender(() => {
      const viewport = this.viewport().nativeElement;
      const stage = this.stage().nativeElement;
      const screen = this.screen().nativeElement;
      const side = this.side().nativeElement;
      const presenter = this.presenter().nativeElement;
      const controls = this.controls().nativeElement;

      // The offset sizes and positions aren't affected by the scale, nor by the moves above. The screen is placed on
      // the stage, the presenter and controls in the side (which is placed on the stage).
      const box = (element: HTMLElement, parent?: HTMLElement): TBox => ({
        left: element.offsetLeft + (parent?.offsetLeft ?? 0),
        top: element.offsetTop + (parent?.offsetTop ?? 0),
        width: element.offsetWidth,
        height: element.offsetHeight,
      });

      let isEntering = false;
      const observer = new ResizeObserver(() => {
        // Once there's room for them, the lamps are drawn in place the next frame and drop down the frame after
        if (!isEntering && viewport.clientWidth > 0) {
          isEntering = true;
          requestAnimationFrame(() => requestAnimationFrame(() => this.hasLampsEntered.set(true)));
        }

        const style = getComputedStyle(stage);
        this.layout.set({
          width: viewport.clientWidth,
          height: viewport.clientHeight,
          stage: {
            width: stage.offsetWidth,
            height: stage.offsetHeight,
            padding: parseFloat(style.paddingRight),
            gap: parseFloat(style.columnGap),
          },
          screen: box(screen),
          presenter: box(presenter, side),
          presenterContentTop: (presenter.firstElementChild as HTMLElement | null)?.offsetTop ?? 0,
          controls: box(controls, side),
        });
      });
      for (const element of [viewport, stage, screen, side, presenter, controls]) observer.observe(element);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  ngOnInit(): void {
    this.resetWordState();
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (this.phase() !== 'INPUT') return;

    if (event.key === 'Backspace') {
      event.preventDefault();
      this.erase();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      void this.submit();
    }
  }

  type(klank: TLingoKlank) {
    if (this.phase() !== 'INPUT') return;

    const slot = this.input().findIndex((k) => k === undefined);
    if (slot === -1) return;

    this.input.update((input) => input.map((k, i) => (i === slot ? klank : k)));
  }

  erase() {
    if (this.phase() !== 'INPUT') return;

    const slot = this.input().reduce((last, k, i) => (k !== undefined ? i : last), -1);
    if (slot === -1) return;

    this.input.update((input) => input.map((k, i) => (i === slot ? undefined : k)));
  }

  async submit() {
    if (this.phase() !== 'INPUT' || !this.isRowComplete()) return;

    const klanken = this.input() as TLingoKlank[];
    const results = evaluateLingoGuess(klanken, this.answer());
    const guessIndex = this.guesses().length;
    // Klanken in the right place that weren't already known before this guess
    const known = this.hints();
    const isNew = (result: TLingoTileResult, i: number) => result === 'CORRECT' && !known.has(i);

    this.phase.set('EVALUATING');
    this.guesses.update((guesses) => [...guesses, { klanken, results, revealed: 0 }]);
    this.presenterMood.set('THINKING');
    await delay(REVEAL_DELAY);

    for (const [index, result] of results.entries()) {
      AudioEffectPlayer.play(result === 'ABSENT' ? 'POP' : 'BUBBLE_POP');
      this.guesses.update((guesses) => guesses.map((g, i) => (i === guessIndex ? { ...g, revealed: g.revealed + 1 } : g)));
      // Excited the moment a new klank is found, until the next guess
      if (isNew(result, index)) this.presenterMood.set('HAPPY');
      await delay(TILE_REVEAL_INTERVAL);
    }

    await delay(300);

    if (results.every((r) => r === 'CORRECT')) {
      await this.win();
    } else if (this.guesses().length >= this.attempts()) {
      await this.lose();
    } else {
      await delay(500);
      // Back to neutral as the keyboard comes back for the next guess
      this.presenterMood.set('IDLE');
      this.resetInput();
      this.phase.set('INPUT');
    }
  }

  private async win() {
    this.phase.set('WON');
    this.presenterMood.set('CHEERING');
    AudioEffectPlayer.play('CORRECT');
    this.overlayFeedback().showCorrect();
    await delay(400);
    await this.pronounce();
  }

  private async lose() {
    AudioEffectPlayer.play('INCORRECT');
    this.overlayFeedback().showIncorrect();
    await delay(400);
    this.phase.set('LOST');
    this.presenterMood.set('SAD');
    await this.pronounce();
  }

  private async pronounce() {
    const audio = this.assignment().audio ?? { pronounce: this.assignment().word };
    if (typeof audio === 'string') {
      await AudioPlayer.play(audio);
    } else {
      await this.$pronunciationService.tryPronounce(audio.pronounce, this.destroyRef);
    }
  }

  next() {
    if (this.isLast()) {
      this.finished.emit();
      return;
    }

    this.index.update((i) => i + 1);
    this.resetWordState();
  }

  private resetWordState() {
    this.guesses.set([]);
    this.resetInput();
    this.phase.set('INPUT');
    this.presenterMood.set('IDLE');
  }

  private resetInput() {
    this.input.set(this.answer().map(() => undefined));
  }

  /** `animate` flips the tiles over when their result is revealed, and pops typed tiles in. */
  tileClasses(tile: TLingoTile, animate = true): string {
    const classes = [TILE, TILE_TEXT, TILE_STATES[tile.state]];

    if (animate && (tile.state === 'CORRECT' || tile.state === 'PRESENT' || tile.state === 'ABSENT')) classes.push('anim-flip-reveal');
    if (animate && tile.state === 'TYPED') classes.push('anim-pop-in');

    return classes.join(' ');
  }
}
